import {
    KentRecordType,
    KentRecordSubType
} from "./KentRecordTypes";
import { IStyleFactory } from "esdm-map/dist/layers/vector/styles/IStyleFactory";
import Feature from "ol/Feature";
import GeometryType from "ol/geom/GeometryType"
import Style, { StyleFunction } from "ol/style/Style";
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import RegularShape from 'ol/style/RegularShape';
import * as olHas from 'ol/has';

export class HbsmrStyleFactory implements IStyleFactory {

    public getStyle(recordType: string, recordSubType: string = null): (Style | Style[] | StyleFunction)
    {
        let style: (Style | Style[] | StyleFunction) = null;

        //try and resolve sub type first
        if (recordSubType !== null) {
            switch (recordSubType) {
                case KentRecordSubType.ConservationArea:
                    style = this.ConservationArea();
                    break;

                case KentRecordSubType.ScheduledMonument:
                    style = this.ScheduledMonument();
                    break;
            
                case KentRecordSubType.RegisteredParksAndGardens:
                    style = this.RegisteredParksAndGardens();
                    break;

                case KentRecordSubType.HistoricParksAndGardens:
                    style = this.HistoricParksAndGardens();
                    break;

                case KentRecordSubType.ListedBuilding:
                    style = this.ListedBuilding();
                    break;
            }

        }

        if (style === null) {
            switch (recordType) {
                case KentRecordType.Monument:
                    style = this.MonumentStyle();
                    break;

                case KentRecordType.Designation:
                    style = this.Designation();
                    break;

                case KentRecordType.HistoricLandscapeCharacterisation:
                    style = this.HLCStyle();
                    break;
            }
        }

        return style;
    }

    public getSelectedStyle(recordType: KentRecordType): (Style | Style[] | StyleFunction) {
        let style: (Style | Style[] | StyleFunction) = null;

        switch (recordType) {
            case KentRecordType.Monument:
                style = this.MonumentSelectedStyle();
                break;

            case KentRecordType.Designation:
                style = this.DesignationSelected();
                break;
        }

        return style;
    }

    private CommonStyle(fillColour: string): Style[] {
        let fill = new Fill({
            color: fillColour
        });
        let stroke = new Stroke({
            color: 'rgba(255,255,255,1)',
            width: 3
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: stroke,
                    radius: 12
                }),
                fill: fill,
                stroke: stroke
            })
        ];
    }
    
    private MonumentStyle(): StyleFunction {
        return function (feature: Feature) {
            let subtypecode = feature.get('recsubtypecode');
            // Monument – Circle - also used as default
            let iconChr: string = '\uf111';
            switch (subtypecode) {
                 //Listed Building – Landmark
                case 'LB':
                    iconChr = '\uf66f';
                    break;

                //Building – Building
                case 'BLD':
                    iconChr = '\uf1ad';
                    break;

                //Findspot – Star
                case 'FS':
                    iconChr = '\uf005';
                    break;

                //Farmstead – Triangle
                case 'FRM':
                    iconChr = '\uf2ec';
                    break;

                //Landscape – Trees
                case 'LND':
                    iconChr = '\uf724';
                    break;

                //Hedgerow – Tree-deciduous
                case 'HED':
                    iconChr = '\uf400';
                    break;

                //Maritime – Sailboat
                case 'MAR':
                    iconChr = '\ue445';
                    break;

                //Place – Thumbtack
                case 'PLA':
                    iconChr = '\uf08d';
                    break;

                //Important geological sequence – Shovel
                case 'GEO':
                    iconChr = '\uf713';
                    break;

                //Pleistocene environmental findspot – skull-cow
                case 'PFS':
                    iconChr = '\uf8de';
                    break;
            }
            
            let symbol = new Text({
                text: iconChr,
                font: '900 20px "Font Awesome 6 Pro"',
                textBaseline: 'bottom',
                offsetY: 11,
                fill: new Fill({
                    color: 'rgba(255,0,0,1)'
                }),
                stroke: new Stroke({
                    color: 'rgba(255,255,255,0.8)',
                    width: 3
                })
            });
            return [new Style({ text: symbol})];
        }

    }

    private ListedBuilding(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case GeometryType.POINT:
                    return new Style({
                        text: new Text({
                            text: '\uf66f',
                            font: '900 20px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(178,0,255,1)' }),
                            stroke: new Stroke({
                                color: 'rgba(255,255,255,0.8)',
                                width: 3
                            })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({
                            color: 'rgba(178,0,255,0.4)'
                        }),
                        stroke: new Stroke({
                            color: 'rgba(178,0,255,1)',
                            width: 2
                        })
                    });
            }
        };
    }
    
    private Designation(): Style[] {
        return this.CommonStyle('rgba(132, 71, 15,0.4)');
    }

    private ConservationArea(): Style[] {
        //return this.CommonStyle('rgba(27, 15, 220, 0.4)');   
        let fill = new Fill({
            color: 'rgba(249, 122, 223, 0.4)'
        });
        let stroke = new Stroke({
            color: 'rgba(249, 122, 223, 1)',
            width: 2
        });

        return [new Style({
            image: new RegularShape({
                fill: fill,
                stroke: stroke,
                points: 5,
                radius: 8,
                radius2: 4,
                angle: 0
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private ScheduledMonument(): Style[] {       
        let fill = new Fill({
            color: 'rgba(32,77, 166, 0.4)'
        });
        let stroke = new Stroke({
            color: 'rgba(32,77, 166, 1)',
            width: 2
        });

        return [new Style({
            image: new RegularShape({
                fill: fill,
                stroke: stroke,
                points: 5,
                radius: 8,
                radius2: 4,
                angle: 0
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private RegisteredParksAndGardens(): Style[] {
        //return this.CommonStyle('rgba(45, 123, 9,0.5)');   
        let fill = new Fill({
            color: 'rgba(255, 170, 61, 0.4)'
        });

        let stroke = new Stroke({
            color: 'rgba(255, 170, 61, 1)',
            width: 2
        });

        return [new Style({
            image: new RegularShape({
                fill: fill,
                stroke: stroke,
                points: 5,
                radius: 8,
                radius2: 4,
                angle: 0
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private HistoricParksAndGardens(): Style[] {
        let fill = new Fill({
            color: 'rgba(45, 115, 9, 0.4)'
        });

        let stroke = new Stroke({
            color: 'rgba(45, 115, 9, 1)',
            width: 2
        });

        return [new Style({
            image: new RegularShape({
                fill: fill,
                stroke: stroke,
                points: 5,
                radius: 8,
                radius2: 4,
                angle: 0
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private HLCStyle(): StyleFunction {
        return function (feature: Feature) {
            let stroke = new Stroke({
                color: 'rgba(45, 45, 45, 1)',
                width: 2
            });

            let typecode: number = parseInt(feature.get('HLTYPE'));
            let color: string = '#fff';
            
            switch (typecode) {
                //1 - Field Patterns
                case 1:
                    color = 'rgba(208, 178, 142, 0.6)';
                    break;

                //2 - Commons
                case 2:
                    color = 'rgba(196, 182, 197, 0.6)';
                    break;

                //3 - Horticulture
                case 3:
                    color = 'rgba(254, 252, 213, 0.6)';
                    break;

                //4 - Woodland
                case 4:
                    color = 'rgba(208, 205, 153, 0.6)';
                    break;

                //5 - Reclaimed Marsh
                case 5:
                    color = 'rgba(243, 238, 176, 0.6)';
                    break;

                //6 - Downland
                case 6:
                    color = 'rgba(170, 129, 89, 0.6)';
                    break;

                //7 - Valley Floor and Water Management
                case 7:
                    color = 'rgba(171, 151, 91, 0.6)';
                    break;

                //8 - Coastal
                case 8:
                    color = 'rgba(207, 149, 130, 0.6)';
                    break;

                //9 - Settlements
                case 9:
                    color = 'rgba(245, 204, 151, 0.6)';
                    break;

                //10 - Parkland and Designed Landscape
                case 10:
                    color = 'rgba(193, 200, 145, 0.6)';
                    break;

                //11 - Recreation
                case 11:
                    color = 'rgba(152, 172, 150, 0.6)';
                    break;

                //12 - Extractive and Other Industry
                case 12:
                    color = 'rgba(172, 173, 90, 0.6)';
                    break;

                //13 - Inland Communication
                case 13:
                    color = 'rgba(77, 77, 77, 0.6)';
                    break;

                //14 - Military and Defence
                case 14:
                    color = 'rgba(218, 187, 104, 0.6)';
                    break;

            }
            
            let fill = new Fill({
                color: color
            });

            return [new Style({
                image: new RegularShape({
                    fill: fill,
                    stroke: stroke,
                    points: 5,
                    radius: 8,
                    radius2: 4,
                    angle: 0
                }),
                fill: fill,
                stroke: stroke
            })];
        }

    }
    
    private Selected(): Style[] {
        let fill = new Fill({
            color: 'rgba(63, 211, 0, 0.6)'
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: new Stroke({
                        color: 'rgba(76, 255, 0, 0.6)',
                        width: 5
                    }),
                    radius: 12
                }),
                fill: fill,
                stroke: new Stroke({
                    color: 'rgba(76, 255, 0, 0.8)',
                    width: 5
                })
            })
        ];
    }

    private DesignationSelected(): StyleFunction {
        return function (feature: Feature): Style {
            let subtypecode = feature.get('recsubtypecode');
            let fill = new Fill({
                color: 'rgba(63, 211, 0, 0.6)'
            });
            let style = new Style({
                image: new Circle({
                    fill: fill,
                    stroke: new Stroke({
                        color: 'rgba(76, 255, 0, 0.6)',
                        width: 5
                    }),
                    radius: 12
                }),
                fill: fill,
                stroke: new Stroke({
                    color: 'rgba(76, 255, 0, 0.8)',
                    width: 5
                })
            });

            switch (subtypecode) {
                case 'LB':
                    switch (feature.getGeometry().getType()) {
                        case GeometryType.POINT:
                            return new Style({
                                text: new Text({
                                    text: '\uf66f',
                                    font: '900 20px "Font Awesome 6 Pro"',
                                    textBaseline: 'bottom',
                                    offsetY: 6,
                                    fill: new Fill({ color: 'rgba(76, 255, 0, 1)' }),
                                    stroke: new Stroke({
                                        color: 'rgba(0,0,0,0.8)',
                                        width: 5
                                    })
                                })
                            });
                        default:
                            return style;
                    }
                default:
                    return style;
            }
            
        }
    }

    private MonumentSelectedStyle(): StyleFunction {
        return function (feature: Feature) {
            let subtypecode = feature.get('recsubtypecode');
            // Monument – Circle - also used as default
            let iconChr: string = '\uf111';
            switch (subtypecode) {
                //Listed Building – Landmark
                case 'LB':
                    iconChr = '\uf66f';
                    break;

                //Building – Building
                case 'BLD':
                    iconChr = '\uf1ad';
                    break;

                //Findspot – Star
                case 'FS':
                    iconChr = '\uf005';
                    break;

                //Farmstead – Triangle
                case 'FRM':
                    iconChr = '\uf2ec';
                    break;

                //Landscape – Trees
                case 'LND':
                    iconChr = '\uf724';
                    break;

                //Hedgerow – Tree-deciduous
                case 'HED':
                    iconChr = '\uf400';
                    break;

                //Maritime – Sailboat
                case 'MAR':
                    iconChr = '\ue445';
                    break;

                //Place – Thumbtack
                case 'PLA':
                    iconChr = '\uf08d';
                    break;

                //Important geological sequence – Shovel
                case 'GEO':
                    iconChr = '\uf713';
                    break;

                //Pleistocene environmental findspot – skull-cow
                case 'PFS':
                    iconChr = '\uf8de';
                    break;
            }

            let symbol = new Text({
                text: iconChr,
                font: '900 20px "Font Awesome 6 Pro"',
                textBaseline: 'bottom',
                offsetY: 11,
                fill: new Fill({
                    color: 'rgba(255,255,0,1)'
                }),
                stroke: new Stroke({
                    color: 'rgba(0,0,0,0.8)',
                    width: 5
                })
            });
            return [new Style({ text: symbol })];
        }

    }

}
